/**
 * NfseDetailDialog — detalhe de uma NFS-e: status, valores, timeline
 * (nfse_events append-only), download (XML/DANFSE via URLs assinadas),
 * cancelamento com motivo e reconciliação de notas presas.
 */
import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Download, ExternalLink, FileText, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { fetchDanfse, useNfse, useNfseEvents } from '@/hooks/useNfse';
import { NFSE_EVENT_META, NFSE_STATUS_META, type NfseDocument } from '@/types/fiscal';
import { NFSE_FRENTE2_ENABLED } from '@/lib/nfseFlags';
// NOTA: o DANFSE agora é o OFICIAL do ADN (via fetchDanfse → pdf_url). O gerador
// jsPDF de src/lib/danfse.ts segue no repo, porém INATIVO (não é o layout oficial) —
// reservado p/ um futuro fluxo ME. Por isso não é mais importado aqui.

const brl = (n: number | null | undefined) =>
  (Number(n ?? 0)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const toneClass: Record<string, string> = {
  neutral: 'bg-muted text-muted-foreground',
  progress: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  error: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
};

const dotClass: Record<string, string> = {
  neutral: 'bg-muted-foreground/50',
  progress: 'bg-blue-500',
  success: 'bg-emerald-500',
  error: 'bg-red-500',
};

export function NfseDetailDialog({
  nfse, open, onOpenChange,
}: {
  nfse: NfseDocument;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { cancel, reconcile } = useNfse();
  const { data: events = [] } = useNfseEvents(open ? nfse.id : null);
  const [motivo, setMotivo] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [danfseBusy, setDanfseBusy] = useState(false);

  const doDanfse = async () => {
    setDanfseBusy(true);
    try {
      // DANFSE OFICIAL do ADN (nfse-danfse busca no ADN via relay e devolve a URL).
      const res = await fetchDanfse(nfse.id);
      const url = res?.pdf_url;
      if (url) window.open(url, '_blank', 'noopener');
      else toast.error('DANFSE oficial indisponível no momento. Tente novamente em instantes.');
    } catch (e) {
      toast.error(`Não foi possível obter o DANFSE (${(e as Error)?.message ?? 'erro'}).`);
    } finally {
      setDanfseBusy(false);
    }
  };

  const meta = NFSE_STATUS_META[nfse.status];

  const doDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetchDanfse(nfse.id);
      if (!res) { toast.error('Não foi possível gerar os links.'); return; }
      const url = res.xml_nfse_url ?? res.pdf_url ?? res.consulta_url ?? res.qr_code_url;
      if (url) window.open(url, '_blank', 'noopener');
      else toast.info('Documentos ainda não disponíveis.');
    } finally {
      setDownloading(false);
    }
  };

  const doCancel = async () => {
    if (motivo.trim().length < 3) { toast.error('Descreva o motivo do cancelamento.'); return; }
    try {
      await cancel.mutateAsync({ nfse_id: nfse.id, motivo: motivo.trim() });
      toast.success('Cancelamento solicitado.');
      setShowCancel(false);
    } catch (e) {
      toast.error(`Não foi possível cancelar (${(e as Error)?.message ?? 'erro'}).`);
    }
  };

  const doReconcile = async () => {
    try {
      const r = (await reconcile.mutateAsync(nfse.id)) as { status?: string; note?: string } | null;
      // A reconciliação volta 200 mesmo quando NÃO localizou a nota → só reportar
      // sucesso se o provedor resolveu o estado (authorized/cancelled).
      const resolved = r?.status === 'authorized' || r?.status === 'cancelled';
      if (resolved) toast.success('Nota reconciliada com o provedor.');
      else toast.info(r?.note ?? 'Nota ainda não localizada no provedor.');
    } catch (e) {
      toast.error(`Falha na reconciliação (${(e as Error)?.message ?? 'erro'}).`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            NFS-e
            <Badge className={toneClass[meta.tone]}>{meta.label}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <span className="text-muted-foreground">Valor</span>
            <span className="text-right font-medium">{brl(nfse.valor_servico)}</span>
            <span className="text-muted-foreground">ISS</span>
            <span className="text-right">{brl(nfse.valor_iss)}{nfse.iss_retido ? ' (retido)' : ''}</span>
            <span className="text-muted-foreground">Líquido</span>
            <span className="text-right">{brl(nfse.valor_liquido)}</span>
            {nfse.numero && (<><span className="text-muted-foreground">Número</span><span className="text-right">{nfse.numero}</span></>)}
            {nfse.chave_acesso && (
              <><span className="text-muted-foreground">Chave</span>
                <span className="break-all text-right font-mono text-xs">{nfse.chave_acesso}</span></>
            )}
          </div>

          {nfse.status === 'rejected' && nfse.rejection_message && (
            <p className="rounded-md bg-red-50 p-2 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-300">
              {nfse.rejection_code}: {nfse.rejection_message}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            {nfse.status === 'authorized' && (
              <Button3D size="sm" onClick={doDanfse} disabled={danfseBusy}>
                {danfseBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
                DANFSE (PDF)
              </Button3D>
            )}
            {nfse.status === 'authorized' && (
              <Button size="sm" variant="outline" onClick={doDownload} disabled={downloading}>
                {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                XML
              </Button>
            )}
            {nfse.qr_code_url && (
              <Button size="sm" variant="outline" asChild>
                <a href={nfse.qr_code_url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" /> Verificar
                </a>
              </Button>
            )}
            {/* Frente 2 (reconciliação/cancelamento) fica oculta até um round-trip
                real de homologação prová-la — flag NFSE_FRENTE2_ENABLED. */}
            {NFSE_FRENTE2_ENABLED && nfse.status === 'unknown_reconciliation_required' && (
              <Button size="sm" variant="outline" onClick={doReconcile} disabled={reconcile.isPending}>
                {reconcile.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Reconciliar
              </Button>
            )}
            {NFSE_FRENTE2_ENABLED && nfse.status === 'authorized' && (
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setShowCancel((v) => !v)}>
                <XCircle className="mr-2 h-4 w-4" /> Cancelar
              </Button>
            )}
          </div>

          {NFSE_FRENTE2_ENABLED && showCancel && (
            <div className="space-y-2 rounded-md border p-3">
              <Label htmlFor="motivo">Motivo do cancelamento</Label>
              <Textarea id="motivo" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
              <Button3D color="red" size="sm" onClick={doCancel} disabled={cancel.isPending}>
                {cancel.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Confirmar cancelamento
              </Button3D>
            </div>
          )}

          <Separator />

          <div>
            <p className="mb-2 font-medium">Histórico</p>
            <ol className="space-y-1.5">
              {events.map((ev) => {
                const em = NFSE_EVENT_META[ev.type];
                return (
                  <li key={ev.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="flex items-center gap-2">
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClass[em?.tone ?? 'neutral']}`} />
                      {em?.label ?? ev.type}
                    </span>
                    <span className="text-muted-foreground">
                      {ev.created_at ? new Date(ev.created_at).toLocaleString('pt-BR') : ''}
                    </span>
                  </li>
                );
              })}
              {events.length === 0 && <li className="text-xs text-muted-foreground">Sem eventos ainda.</li>}
            </ol>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
